import { useEffect, useRef, useState } from "react";
import { useChat } from "@/context/ChatContext";
import { useLang } from "@/i18n/LanguageContext";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Sparkles, RotateCcw, AlertCircle } from "lucide-react";
import ChatMessage, { ChatTypingIndicator } from "@/components/chat/ChatMessage";

export default function ChatDrawer() {
  const { t } = useLang();
  const {
    open,
    closeChat,
    messages,
    isThinking,
    error,
    sendMessage,
    clearChat,
    consumePrefill,
    pendingPrefill,
    analysis,
  } = useChat();
  const [input, setInput] = useState("");
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      scrollerRef.current?.scrollTo({
        top: scrollerRef.current.scrollHeight,
        behavior: "smooth",
      });
    });
  }, [open, messages, isThinking]);

  // If a prefill was queued by a chip action, set it in the input
  useEffect(() => {
    if (!open || !pendingPrefill) return;
    setInput(pendingPrefill);
    consumePrefill();
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [open, pendingPrefill, consumePrefill]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeChat();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeChat]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    sendMessage(text);
  };

  const showSuggestions = messages.length === 0 && !isThinking;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeChat}
            className="fixed inset-0 z-40 bg-zinc-900/30 backdrop-blur-[2px]"
            data-testid="chat-backdrop"
          />

          {/* Drawer */}
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 32 }}
            className="fixed right-0 top-0 z-50 flex h-[100dvh] w-full max-w-[460px] flex-col border-l border-zinc-200 bg-white shadow-[-24px_0_60px_-20px_rgba(81,75,186,0.25)]"
            data-testid="chat-drawer"
            role="dialog"
            aria-label="Andy chat"
          >
            <Header onClose={closeChat} onReset={clearChat} hasMessages={messages.length > 0} analysis={analysis} t={t} />

            <div
              ref={scrollerRef}
              className="flex-1 space-y-4 overflow-y-auto bg-gradient-to-b from-white via-white to-[color:var(--sentinel-tint)]/30 px-5 py-6"
            >
              {messages.length === 0 && (
                <EmptyState t={t} analysis={analysis} />
              )}
              {messages.map((m, i) => (
                <ChatMessage key={i} message={m} idx={i} />
              ))}
              {isThinking && <ChatTypingIndicator />}
              {error && (
                <div
                  className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-3 text-xs text-red-700"
                  data-testid="chat-error"
                >
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{t.chat.errors.network}</span>
                </div>
              )}
            </div>

            {showSuggestions && (
              <div className="border-t border-zinc-100 bg-white px-5 py-3" data-testid="chat-suggestions">
                <div className="flex flex-wrap gap-2">
                  {t.chat.suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(s)}
                      className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-xs font-medium text-zinc-700 transition hover:border-[color:var(--sentinel)] hover:bg-[color:var(--sentinel-tint)] hover:text-[color:var(--sentinel-darker)]"
                      data-testid={`chat-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="border-t border-zinc-200 bg-white px-5 py-4"
              data-testid="chat-input-form"
            >
              <div className="flex items-end gap-2 rounded-2xl border border-zinc-200 bg-white px-3 py-2 transition focus-within:border-[color:var(--sentinel)]">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  placeholder={t.chat.placeholder}
                  rows={1}
                  className="max-h-32 flex-1 resize-none bg-transparent py-1.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400"
                  data-testid="chat-input"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isThinking}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--sentinel)] text-white transition hover:bg-[color:var(--sentinel-dark)] disabled:cursor-not-allowed disabled:bg-zinc-300"
                  data-testid="chat-send"
                  aria-label={t.chat.send}
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2 text-[10px] leading-relaxed text-zinc-400">
                {t.chat.disclaimer}
              </p>
            </form>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function Header({ onClose, onReset, hasMessages, analysis, t }) {
  return (
    <header className="flex items-center justify-between gap-3 border-b border-zinc-200 bg-white px-5 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <div className="relative">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-2xl text-white"
            style={{ background: "linear-gradient(135deg, #514bba 0%, #514bbacc 100%)" }}
          >
            <Sparkles className="h-4 w-4" />
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-zinc-900">
            {t.chat.title}
          </p>
          <p className="truncate text-[11px] text-zinc-500">
            {analysis ? t.chat.subtitleAnalysis : t.chat.subtitle}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {hasMessages && (
          <button
            onClick={onReset}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900"
            data-testid="chat-reset"
            aria-label={t.chat.reset}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          onClick={onClose}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
          data-testid="chat-close"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}

function EmptyState({ t, analysis }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-start gap-3 rounded-3xl border border-dashed border-[color:var(--sentinel-accent)] bg-white p-5"
      data-testid="chat-empty"
    >
      <span className="overline text-[10px]">{t.chat.greetingOverline}</span>
      <h3 className="text-xl text-zinc-900">
        {analysis ? t.chat.greetingWithAnalysis : t.chat.greeting}
      </h3>
      <p className="text-sm leading-relaxed text-zinc-600">
        {analysis ? t.chat.greetingBodyAnalysis : t.chat.greetingBody}
      </p>
    </motion.div>
  );
}
